import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '@umanforge/common';
import SubmitButton from '@common_components/buttons/submitButton.component';
import ConfirmationModal from '@common_components/modals/confirmationModal.component';
import BaseModal from '@common_components/modals/baseModal.component';
import TextInput from '@common_components/inputs/textInput.component';
import SelectInput from '@common_components/inputs/selects/selectInput.component';
import { ExtractionSavedView, ExtractionSavedViewColumnInput, ExtractionSavedViewFilterInput, ExtractionSavedViewInput, ExtractionSavedViewUpdate } from '../../interfaces/extractionSavedView.interface';
import { ExtractionSavedViewService } from 'services/extractionSavedView.service';

interface SavedViewsManagerProps {
  orientation?: 'vertical' | 'horizontal';
  getCurrentFiltersAndColumns?: () => { filters: ExtractionSavedViewFilterInput[]; columns: ExtractionSavedViewColumnInput[] };
  onApplyView?: ( view: ExtractionSavedView ) => void;
  selectedViewId?: string | null;
  onChangeSelectedViewId?: ( id: string | null ) => void;
  onClearSelection?: () => void;
}
const SavedViewsManager: React.FC<SavedViewsManagerProps & { dataType?: 'filters' | 'columns' }> = ( { orientation = 'horizontal', dataType = 'filters', getCurrentFiltersAndColumns, onApplyView, selectedViewId, onChangeSelectedViewId, onClearSelection } ) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );

  const [ savedViews, setSavedViews ] = useState<ExtractionSavedView[]>( [] );
  const [ selectedSavedViewId, setSelectedSavedViewId ] = useState<string | null>( null );
  const [ isDeleteViewModalOpen, setIsDeleteViewModalOpen ] = useState<boolean>( false );
  const [ isSaveViewModalOpen, setIsSaveViewModalOpen ] = useState<boolean>( false );
  const [ isUpdateViewModalOpen, setIsUpdateViewModalOpen ] = useState<boolean>( false );
  const [ viewNameDraft, setViewNameDraft ] = useState<string>( '' );
  const [ isPublicDraft, setIsPublicDraft ] = useState<boolean>( false );

  useEffect( () => {
    const fetchSavedViews = async () => {
      if ( !token ) {
        setSavedViews( [] );
        return;
      }
      const views = await ExtractionSavedViewService.list( dataType, token, dispatch );
      setSavedViews( views );
    };

    fetchSavedViews();
  }, [ token, dispatch, dataType ] );

  useEffect( () => {
    if ( selectedViewId !== undefined ) {
      setSelectedSavedViewId( selectedViewId );
    }
  }, [ selectedViewId ] );

  const areViewAndCurrentEqual = ( view: ExtractionSavedView ): boolean => {
    if ( !getCurrentFiltersAndColumns ) {
      return false;
    }

    const current = getCurrentFiltersAndColumns();

    if ( dataType === 'columns' ) {
      const currentColumns = ( current.columns || [] ).map( c => { return c.key; } ).sort();
      const savedColumns = ( view.columns || [] ).map( c => { return c.key; } ).sort();
      return JSON.stringify( currentColumns ) === JSON.stringify( savedColumns );
    }

    if ( !view.filters || view.filters.length === 0 ) {
      return false;
    }

    const normalize = ( list: ExtractionSavedViewFilterInput[] ): ExtractionSavedViewFilterInput[] => {
      const idArrayKeys = [ 'clientIds', 'siteIds', 'sectorIds', 'eventManagerIds', 'participantIds', 'themeIds' ];

      const normalizeValue = ( key: string, value: string | undefined ): string => {
        if ( value == null ) {
          return '';
        }

        if ( idArrayKeys.includes( key ) ) {
          const parsed = JSON.parse( value ) as unknown;
          if ( Array.isArray( parsed ) ) {
            const sorted = [ ...parsed ].map( String ).sort();
            return JSON.stringify( sorted );
          }
        }

        return String( value );
      };

      return [ ...list ].sort( ( a, b ) => {
        if ( a.key === b.key ) {
          const aNorm = normalizeValue( a.key, a.value );
          const bNorm = normalizeValue( b.key, b.value );
          return aNorm.localeCompare( bNorm );
        }
        return a.key.localeCompare( b.key );
      } );
    };

    const currentFilters = normalize( current.filters || [] );
    const savedFilters = normalize( view.filters.map( f => { return { key: f.key, value: f.value }; } ) );

    return JSON.stringify( currentFilters ) === JSON.stringify( savedFilters );
  };

  const hasPendingChanges = ( (): boolean => {
    if ( !selectedSavedViewId || !getCurrentFiltersAndColumns ) {
      return false;
    }

    const selected = savedViews.find( view => { return view.id === selectedSavedViewId; } );
    if ( !selected ) {
      return false;
    }

    return !areViewAndCurrentEqual( selected );
  } )();

  const hasAnySelectedFilters = ( (): boolean => {
    if ( !getCurrentFiltersAndColumns ) {
      return false;
    }

    const current = getCurrentFiltersAndColumns();

    if ( dataType === 'columns' ) {
      const columns = current.columns ?? [];
      return columns.length > 0;
    }

    const filters = current.filters ?? [];
    // Ignore the technical "multipleSheets" flag; require at least one real filter
    return filters.some( filter => { return filter.key !== 'multipleSheets'; } );
  } )();

  const handleChangeSavedView = ( id: string | null ) => {
    setSelectedSavedViewId( id );

    if ( onChangeSelectedViewId ) {
      onChangeSelectedViewId( id );
    }

    if ( !id || !onApplyView ) {
      return;
    }

    const selected = savedViews.find( view => { return view.id === id; } );
    if ( selected ) {
      onApplyView( selected );
    }
  };

  const handleSaveView = () => {
    if ( !token || !hasAnySelectedFilters ) {
      return;
    }
    setViewNameDraft( '' );
    setIsPublicDraft( false );
    setIsSaveViewModalOpen( true );
  };

  const handleUpdateView = () => {
    if ( !token || !selectedSavedViewId ) {
      return;
    }

    const current = savedViews.find( view => { return view.id === selectedSavedViewId; } );
    const currentName = current?.name || '';
    setViewNameDraft( currentName );
    // Keep views private by default; public flag is no longer editable from the UI
    setIsPublicDraft( false );
    setIsUpdateViewModalOpen( true );
  };

  const handleDeleteView = () => {
    if ( !token || !selectedSavedViewId ) {
      return;
    }
    setIsDeleteViewModalOpen( true );
  };

  const handleConfirmDeleteView = async () => {
    if ( !token || !selectedSavedViewId ) {
      setIsDeleteViewModalOpen( false );
      return;
    }

    const ok = await ExtractionSavedViewService.delete( selectedSavedViewId, token, dispatch );
    if ( ok ) {
      const views = await ExtractionSavedViewService.list( dataType, token, dispatch );
      setSavedViews( views );
      setSelectedSavedViewId( null );
      if ( onChangeSelectedViewId ) {
        onChangeSelectedViewId( null );
      }
      if ( onClearSelection ) {
        onClearSelection();
      }
    }
    setIsDeleteViewModalOpen( false );
  };

  const handleConfirmSaveView = async () => {
    if ( !token || !viewNameDraft.trim() ) {
      setIsSaveViewModalOpen( false );
      return;
    }

    const current = getCurrentFiltersAndColumns ? getCurrentFiltersAndColumns() : { filters: [], columns: [] };
    let { filters, columns } = current;

    if ( dataType === 'filters' ) {
      columns = [];
    } else if ( dataType === 'columns' ) {
      filters = [];
    }

    const payload: ExtractionSavedViewInput = {
      name: viewNameDraft.trim(),
      public: isPublicDraft ? 1 : 0,
      ...( dataType === 'filters' && filters.length > 0 ? { filters } : {} ),
      ...( dataType === 'columns' && columns.length > 0 ? { columns } : {} ),
    };

    const result = await ExtractionSavedViewService.create( payload, token, dispatch );
    if ( result.id ) {
      const views = await ExtractionSavedViewService.list( dataType, token, dispatch );
      setSavedViews( views );
      setSelectedSavedViewId( result.id );
    }
    setIsSaveViewModalOpen( false );
  };

  const handleConfirmUpdateView = async () => {
    if ( !token || !selectedSavedViewId || !viewNameDraft.trim() ) {
      setIsUpdateViewModalOpen( false );
      return;
    }

    const current = getCurrentFiltersAndColumns ? getCurrentFiltersAndColumns() : { filters: [], columns: [] };
    let { filters, columns } = current;

    if ( dataType === 'filters' ) {
      columns = [];
    } else if ( dataType === 'columns' ) {
      filters = [];
    }

    const payload: ExtractionSavedViewUpdate = {
      name: viewNameDraft.trim(),
      public: isPublicDraft ? 1 : 0,
      ...( dataType === 'filters' && filters.length > 0 ? { filters } : {} ),
      ...( dataType === 'columns' && columns.length > 0 ? { columns } : {} ),
    };

    const ok = await ExtractionSavedViewService.update( selectedSavedViewId, payload, token, dispatch );
    if ( ok ) {
      const views = await ExtractionSavedViewService.list( dataType, token, dispatch );
      setSavedViews( views );
    }
    setIsUpdateViewModalOpen( false );
  };

  return (
    <>
      <div className={orientation === 'vertical'
        ? 'flex flex-col gap-2'
        : 'flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full'}>
        <div className="flex flex-col gap-2 border border-gray-200 rounded-lg p-3 w-full">
          {orientation === 'vertical' && (
            <SelectInput
              textSize="w-full"
              selected={selectedSavedViewId ?? ''}
              placeholder={t( 'translation:select_view' )}
              fetchOptions={() => {
                return Promise.resolve( [
                  {
                    options: savedViews.map( view => {
                      return {
                        id: view.id,
                        label: view.name,
                      };
                    } ),
                  },
                ] );
              }}
              refreshTrigger={savedViews.length}
              onSelect={value => { handleChangeSavedView( value || null ); }}
            />
          )}

          {orientation === 'vertical' && (
            <SubmitButton
              label={t( 'translation:save_view_button' )}
              onClick={handleSaveView}
              disable={!token || !!selectedSavedViewId || !hasAnySelectedFilters}
              bgColor="bg-green-base"
              textColor="text-white"
              width="w-full"
            />
          )}

          <div className={orientation === 'horizontal'
            ? 'flex flex-row flex-wrap gap-2 items-stretch w-full'
            : 'flex flex-col gap-2 items-stretch'}>
            {orientation === 'horizontal' && (
              <div className="flex-[2]">
                <SelectInput
                  textSize="w-full"
                  selected={selectedSavedViewId ?? ''}
                  placeholder={t( 'translation:select_view' )}
                  fetchOptions={() => {
                    return Promise.resolve( [
                      {
                        options: savedViews.map( view => {
                          return {
                            id: view.id,
                            label: view.name,
                          };
                        } ),
                      },
                    ] );
                  }}
                  refreshTrigger={savedViews.length}
                  onSelect={value => { handleChangeSavedView( value || null ); }}
                />
              </div>
            )}

            {orientation === 'horizontal' && (
              <div className="flex-1">
                <SubmitButton
                  label={t( 'translation:save_view_button' )}
                  onClick={handleSaveView}
                  disable={!token || !!selectedSavedViewId || !hasAnySelectedFilters}
                  bgColor="bg-green-base"
                  textColor="text-white"
                  width="w-full"
                />
              </div>
            )}

            <div className="flex-1">
              <SubmitButton
                label={t( 'translation:update_view' )}
                onClick={handleUpdateView}
                disable={!selectedSavedViewId || !hasPendingChanges}
                bgColor="bg-blue-base"
                textColor="text-white"
                width="w-full"
              />
            </div>

            <div className="flex-1">
              <SubmitButton
                label={t( 'translation:delete_view_button' )}
                onClick={handleDeleteView}
                disable={!selectedSavedViewId}
                bgColor="bg-red-base"
                textColor="text-white"
                width="w-full"
              />
            </div>
          </div>
        </div>

        {( onClearSelection || onChangeSelectedViewId ) && (
          <button
            type="button"
            className="mt-2 self-start text-blue-base underline text-sm select-none"
            onClick={() => {
              setSelectedSavedViewId( null );
              if ( onChangeSelectedViewId ) {
                onChangeSelectedViewId( null );
              }
              if ( onClearSelection ) {
                onClearSelection();
              }
            }}
          >
            {t( 'translation:clear_selection' )}
          </button>
        )}
      </div>

      <ConfirmationModal
        title={t( 'translation:delete_view_title' )}
        description={t( 'translation:delete_view_confirm' )}
        isOpen={isDeleteViewModalOpen}
        onConfirm={handleConfirmDeleteView}
        onCancel={() => { setIsDeleteViewModalOpen( false ); }}
      />

      <BaseModal
        isOpen={isSaveViewModalOpen}
        onClose={() => { setIsSaveViewModalOpen( false ); }}
        noConfirmationModal={true}
        title={t( 'translation:save_view_title' ) as string}
      >
        <div className="p-6 flex flex-col gap-4">
          <TextInput
            label={t( 'translation:save_view_prompt' ) as string}
            value={viewNameDraft}
            onChange={setViewNameDraft}
            maxCharacter={30}
            isRequired
          />
          <div className="flex justify-end gap-4 mt-4">
            <SubmitButton
              label={t( 'common:cancel' )}
              onClick={() => { setIsSaveViewModalOpen( false ); }}
              bgColor="bg-gray-base"
              textColor="text-black"
              width="w-auto"
            />
            <SubmitButton
              label={t( 'common:save' )}
              onClick={handleConfirmSaveView}
              bgColor="bg-green-base"
              textColor="text-white"
              width="w-auto"
              disable={!viewNameDraft.trim()}
            />
          </div>
        </div>
      </BaseModal>

      <BaseModal
        isOpen={isUpdateViewModalOpen}
        onClose={() => { setIsUpdateViewModalOpen( false ); }}
        noConfirmationModal={true}
        title={t( 'translation:update_view_title' ) as string}
      >
        <div className="p-6 flex flex-col gap-4">
          <TextInput
            label={t( 'translation:update_view_prompt' ) as string}
            value={viewNameDraft}
            onChange={setViewNameDraft}
            maxCharacter={30}
            isRequired
          />
          <div className="flex justify-end gap-4 mt-4">
            <SubmitButton
              label={t( 'common:cancel' )}
              onClick={() => { setIsUpdateViewModalOpen( false ); }}
              bgColor="bg-gray-base"
              textColor="text-black"
              width="w-auto"
            />
            <SubmitButton
              label={t( 'common:save' )}
              onClick={handleConfirmUpdateView}
              bgColor="bg-green-base"
              textColor="text-white"
              width="w-auto"
              disable={!viewNameDraft.trim()}
            />
          </div>
        </div>
      </BaseModal>
    </>
  );
};

export default SavedViewsManager;
